import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * TIME DELTA FLOATER
 *
 * Listens for `wipe-time-delta` window events and renders a
 * short-lived floating "+N min" or "-N min" indicator that drifts up
 * and fades out, like damage/heal numbers in a video game.
 *
 * Fire the event from any write path that changes the current user's
 * time wallet:
 *
 *   window.dispatchEvent(new CustomEvent('wipe-time-delta',
 *     { detail: { seconds: +300 } }));
 *
 * Positive = gained (green), negative = spent (red). Multiple deltas
 * in quick succession stack vertically so you can see a burst.
 */

type Delta = { id: number; seconds: number };

function formatDelta(seconds: number): string {
  const sign = seconds > 0 ? '+' : '−';
  const abs = Math.abs(seconds);
  if (abs >= 86400) {
    const d = Math.floor(abs / 86400);
    return `${sign}${d}d`;
  }
  if (abs >= 3600) {
    const h = Math.floor(abs / 3600);
    return `${sign}${h}h`;
  }
  if (abs >= 60) {
    const m = Math.floor(abs / 60);
    return `${sign}${m}m`;
  }
  return `${sign}${abs}s`;
}

function TimeDeltaFloater() {
  const [deltas, setDeltas] = useState<Delta[]>([]);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ seconds: number }>;
      const s = ce.detail?.seconds ?? 0;
      if (!s) return;
      const id = Date.now() + Math.random();
      setDeltas((prev) => [...prev, { id, seconds: s }]);
      // Remove after animation completes.
      setTimeout(() => {
        setDeltas((prev) => prev.filter((d) => d.id !== id));
      }, 1600);
    };
    window.addEventListener('wipe-time-delta', handler);
    return () => window.removeEventListener('wipe-time-delta', handler);
  }, []);

  if (deltas.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 60,
        right: 140,
        zIndex: 1300,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.25,
        '@keyframes time-float': {
          '0%':   { opacity: 0, transform: 'translateY(8px) scale(0.9)' },
          '15%':  { opacity: 1, transform: 'translateY(0px) scale(1)' },
          '75%':  { opacity: 1, transform: 'translateY(-28px) scale(1)' },
          '100%': { opacity: 0, transform: 'translateY(-44px) scale(0.95)' },
        },
      }}
    >
      {deltas.map((d) => (
        <Box
          key={d.id}
          sx={{
            fontFamily: '"VT323", monospace',
            fontSize: '1.6rem',
            letterSpacing: 1,
            fontWeight: 700,
            color: d.seconds > 0 ? '#4caf50' : '#ff5252',
            textShadow: d.seconds > 0
              ? '0 0 6px rgba(76,175,80,0.6), 0 0 14px rgba(76,175,80,0.3)'
              : '0 0 6px rgba(255,82,82,0.6), 0 0 14px rgba(255,82,82,0.3)',
            animation: 'time-float 1.6s ease-out forwards',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatDelta(d.seconds)}
        </Box>
      ))}
    </Box>
  );
}

export default TimeDeltaFloater;
